import { useState, useRef } from 'react';
import enviro from 'enviro';
import http from 'hub-http/clients/apiClient';
import Raven from 'raven-js';
import I18n from 'I18n';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { openPopup, openTab } from '../../utils/popupUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const FIVE_MINUTES = 300000;
const DELAY_500_MS = 500;
const AUTHORIZE_URL_RE = /^(?!.*\s)[^?]*oauthor\/[^?]*\/authorize-url[^?]*(?:\?.*)?$/;
export const BasicReconnectButton = ({
  buttonComponent: ButtonComponent,
  buttonProps,
  failed: propsFailed,
  loading: propsLoading,
  'data-test-id': dataTestId,
  label,
  onAuthStarted,
  onAuthCancelled,
  onAuthSucceeded,
  timeout = FIVE_MINUTES,
  url
}) => {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestTimerId = useRef(0);
  const intervalTimerId = useRef(0);
  const popupRef = useRef(null);
  const clearTimers = () => {
    if (requestTimerId.current) {
      clearTimeout(requestTimerId.current);
    }
    if (intervalTimerId.current) {
      clearInterval(intervalTimerId.current);
    }
  };

  /**
   * Historically there was only `error` param which could have mostly technical info
   * Also it plays a role of an indicator of unhappy path.
   * The message for customer in this case was something generic and does not have information about the real issue.
   *
   * The param `errorMessage` is introduced for a customer.
   * It should contain the information about the real issue.
   */
  const handleAuthCancelled = ({
    error,
    errorMessage,
    queryParams
  }) => {
    clearTimers();
    setFailed(true);
    setLoading(false);
    onAuthCancelled === null || onAuthCancelled === void 0 || onAuthCancelled({
      error,
      errorMessage,
      queryParams
    });
  };
  const handleAuthSucceeded = authData => {
    clearTimers();
    setFailed(false);
    setLoading(false);
    onAuthSucceeded === null || onAuthSucceeded === void 0 || onAuthSucceeded(authData);
  };
  const handleAuth = ({
    code,
    error,
    errorMessage,
    queryParams
  }) => {
    if (!code) {
      handleAuthCancelled({
        error,
        errorMessage,
        queryParams
      });
    } else {
      handleAuthSucceeded(code);
    }
  };
  const openPopupWindow = popupUrl => {
    //@ts-expect-error authCallback does not exist on type 'Window & typeof globalThis'
    window.authCallback = handleAuth;
    //@ts-expect-error authCancelled does not exist on type 'Window & typeof globalThis'
    window.authCancelled = handleAuthCancelled;
    requestTimerId.current = setTimeout(() => {
      handleAuthCancelled({
        error: null
      });
    }, timeout);
    popupRef.current = enviro.deployed() ? openPopup(popupUrl) : openTab(popupUrl);
    intervalTimerId.current = setInterval(() => {
      let popupIsClosed = !popupRef.current;
      try {
        /**
         * The workaround in case of Cross-Origin-Opener-Policy problem
         */
        popupIsClosed = popupRef.current.closed;
      } catch (err) {
        // do nothing
      }
      if (popupIsClosed) {
        handleAuthCancelled({
          error: null
        });
      }
    }, DELAY_500_MS);
  };
  const handleClick = evt => {
    var _buttonProps$onClick;
    setLoading(true);
    buttonProps === null || buttonProps === void 0 || (_buttonProps$onClick = buttonProps.onClick) === null || _buttonProps$onClick === void 0 || _buttonProps$onClick.call(buttonProps, evt);
    onAuthStarted === null || onAuthStarted === void 0 || onAuthStarted();
    if (AUTHORIZE_URL_RE.test(url)) {
      /**
       * Fetch authorize URL
       */
      http.get(url).then(resp => {
        const popupUrl = (resp === null || resp === void 0 ? void 0 : resp.authorizeUrl) || '';
        if (!popupUrl) {
          throw new Error(`Unable to fetch authorize URL using: ${url}`);
        }
        openPopupWindow(popupUrl);
      }).catch(err => {
        Raven.captureException(err);
        handleAuthCancelled({
          error: err,
          errorMessage: I18n.text('connectionStatusAlerts.fetchAuthorizeUrlError.message')
        });
      });
    } else {
      openPopupWindow(url);
    }
  };
  const updatedButtonProps = Object.assign({}, buttonProps || {}, {
    onClick: handleClick,
    'data-test-id': dataTestId,
    disabled: (buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.disabled) || loading || propsLoading
  });
  const isFailed = failed || propsFailed;
  const isLoading = loading || propsLoading;
  return ButtonComponent ? /*#__PURE__*/_jsx(ButtonComponent, Object.assign({}, updatedButtonProps, {
    children: label
  })) : /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, updatedButtonProps, {
    failed: isFailed,
    loading: isLoading,
    children: label
  }));
};